import 'rc-slider/assets/index.css'
import Big from 'big.js'
import Trans from 'next-translate/Trans'
import useTranslation from 'next-translate/useTranslation'
import { useCallback, useContext, useMemo } from 'react'
import Countdown from 'react-countdown'
import { Controller, useForm } from 'react-hook-form'

import { useExecuteTransactions } from '../hooks/useExecuteTransactions'
import { isTimeAfterNow } from '../utils'

import { AppContext } from './AppContext/AppContext'
import CryptoInputGroup from './CryptoInputGroup/CryptoInputGroup'
import { LockedMETContext } from './LockedMetContext'
import { MetronomeContext } from './MetronomeContext'
import OperationButton from './OperationButton'
import TransactionContext from './TransactionContext'
import UnlockFormDetails from './UnlockFormDetails'

export type UnlockFormProps = {
  tokenId: number
}

function UnlockForm({ tokenId }: UnlockFormProps) {
  const { t } = useTranslation()
  const {
    state: {
      status: { paused },
      wallet: { connected }
    }
  } = useContext(AppContext)

  const {
    disableOnOperation,
    openTransactionDrawer,
    setDisableOnOperation,
    setOperationExecuted
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } = useContext<any>(MetronomeContext) // @TODO: type MetronomeContext

  const { amountLocked, closeOperation, lockedPositions } =
    useContext(LockedMETContext)

  const positionToUnlock = lockedPositions.find(
    lockedPosition => lockedPosition.tokenId === tokenId
  )

  const { metLocker, met } = useContext(LockedMETContext)

  const {
    control,
    formState: { errors },
    handleSubmit
  } = useForm({
    defaultValues: {
      amount: positionToUnlock.lockedAmount,
      token: met
    }
  })

  const { options: transactionOptions } = useContext(TransactionContext)
  const { executeTransactions } = useExecuteTransactions()
  const availableTokens = useMemo(() => [met], [met])

  const onSubmit = useCallback(
    function ({ amount, token }) {
      openTransactionDrawer('unlock')
      closeOperation('unlock')
      setDisableOnOperation(true)
      setOperationExecuted(true)
      const _transactionOptions = {
        useEip1559: transactionOptions.useEip1559
      }

      const { emitter } = metLocker.unlock(
        tokenId,
        isTimeAfterNow(positionToUnlock.unlockTime),
        _transactionOptions
      )

      const operationSubject = met.symbol
      const analyticsId = operationSubject
      executeTransactions(emitter, {
        analytics: {
          events: {
            'error': `Locking failed for ${analyticsId}`,
            'error-timeout': `Locking timeout in wallet for ${analyticsId}`,
            'result': `Locking succeeded for ${analyticsId}`,
            'transactions': `Locking initiated for ${analyticsId}`
          },
          toast: {
            close: `Closing pending tx notification for Locking of ${analyticsId}`,
            reopen: `Reopening Tx Modal for Locking of ${analyticsId}`
          },
          useEip1559: `Using EIP-1559 to Lock MET`
        },
        events: {
          extendedStatus: {
            amount,
            operation: 'unlock',
            operationSubject,
            token,
            totalAmountLocked: amountLocked,
            unlockAmount: positionToUnlock.lockedAmount
          }
        },
        onError() {
          setDisableOnOperation(false)
        },
        onSuccess() {
          setDisableOnOperation(false)
        }
      })
    },
    [
      amountLocked,
      closeOperation,
      executeTransactions,
      met.symbol,
      metLocker,
      openTransactionDrawer,
      positionToUnlock.lockedAmount,
      positionToUnlock.unlockTime,
      setDisableOnOperation,
      setOperationExecuted,
      tokenId,
      transactionOptions.useEip1559
    ]
  )

  const userBalance = positionToUnlock.lockedAmount
  const hasErrors = Object.keys(errors).length > 0

  const earlyUnlockTime = new Date(positionToUnlock.minimumUnlockTime * 1000)
  const expireDate = new Date(positionToUnlock.unlockTime * 1000)
  const before24h = new Date(Date.now()).getTime() < earlyUnlockTime.getTime()
  const beforeExpireDate = new Date(Date.now()).getTime() < expireDate.getTime()
  const canUnlock = !before24h
  const earlyUnlockKey = before24h
    ? '24h'
    : beforeExpireDate
    ? 'expire-date'
    : ''

  return (
    <form className="space-y-6" onSubmit={handleSubmit(onSubmit)}>
      <div>
        {!!earlyUnlockKey && (
          <>
            <h2 className="mb-2 text-lg font-semibold text-white">
              <Trans
                components={[
                  <span className="text-teal-primary" key={0}>
                    <Countdown
                      date={before24h ? earlyUnlockTime : expireDate}
                      daysInHours={true}
                      renderer={function ({ days, hours, minutes, seconds }) {
                        if (days) {
                          return t('n-days', { count: days })
                        }
                        if (hours) {
                          return t('n-hours', { count: hours })
                        }
                        if (minutes) {
                          return t('n-minutes', { count: minutes })
                        }
                        return t('n-seconds', { count: seconds })
                      }}
                    />
                  </span>
                ]}
                i18nKey={`unlock-form.unlock-before-${earlyUnlockKey}`}
              />
            </h2>
            <p className="mb-6 text-sm">
              {t(`unlock-form.unlock-before-${earlyUnlockKey}-description`)}
            </p>
          </>
        )}
        <CryptoInputGroup availableTokens={availableTokens}>
          <div className="bg-blue-secondary border-blue-secondary rounded-lg border px-4 pb-3 pt-4">
            <div className="flex justify-between">
              <Controller
                control={control}
                name="amount"
                render={({ field }) => (
                  <CryptoInputGroup.Input
                    {...field}
                    disabled={disableOnOperation || paused}
                    readOnly
                  />
                )}
                rules={{
                  validate: {
                    earlyUnlock: () => !before24h,
                    greaterThanZero: value => value && Big(value).gt(0),
                    max: value => value && Big(value).lte(userBalance)
                  }
                }}
              />

              <Controller
                control={control}
                name="token"
                render={({ field: { onChange, onBlur } }) => (
                  <CryptoInputGroup.TokenSelector
                    chooseLabel={t('choose-collateral')}
                    disabled={disableOnOperation || paused}
                    onBlur={onBlur}
                    onChange={onChange}
                  />
                )}
                rules={{ required: true }}
              />
            </div>

            <div className="text-gray-4 mt-2 flex items-center justify-between text-sm leading-6">
              <CryptoInputGroup.UsdValue prefix="≈$" />
            </div>
          </div>
        </CryptoInputGroup>
      </div>

      <UnlockFormDetails
        amountToUnlock={positionToUnlock.lockedAmount}
        tokenId={tokenId}
      />

      <OperationButton
        disabled={!connected || hasErrors || !canUnlock}
        type="submit"
      >
        {t('actions.unlock')}
      </OperationButton>
    </form>
  )
}

export default UnlockForm
